import cn from 'clsx'
import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import TextLink from 'components/Luxkit/TextLink'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import Caption from 'components/Luxkit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'

const NumberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.S};

  &.exclusive {
    background-color: ${props => props.theme.palette.product.exclusive};
  }

  &.black {
    background-color: ${props => props.theme.palette.neutral.one};
  }

  &.variant-xs {
    height: ${rem(24)};
    padding: 0 ${rem(6)};
  }

  &.variant-small {
    padding: ${rem(6)};
  }

  &.variant-medium {
    padding: ${rem(6)};
  }

  &.variant-large {
    padding: ${rem(6)} ${rem(8)};
  }

  &.variant-xl {
    padding: ${rem(6)} ${rem(8)};
  }
 `

interface Props {
  link?: boolean;
  rating: number;
  total?: number;
  onlyNumber?: boolean;
  inlineLabel?: boolean;
  hideLink?: boolean;
  variant?: 'xs' | 'small' | 'medium' | 'large' | 'xl';
  ratingSubtitle?: string;
  onReviewSubTitleClick?: () => void;
  className?: string;
}

function NumberRating(props: Props) {
  const {
    link,
    rating,
    total = 0,
    onlyNumber = false,
    inlineLabel = false,
    hideLink = false,
    variant = 'xl',
    ratingSubtitle,
    onReviewSubTitleClick,
    className,
  } = props

  const ratingLabel = useMemo(() => {
    if (rating >= 9.6) return 'Outstanding'
    if (rating >= 9 && rating < 9.6) return 'Exceptional'
    if (rating >= 8.6 && rating < 9) return 'Excellent'
    if (rating >= 8 && rating < 8.6) return 'Very Good'
    if (rating >= 7 && rating < 8) return 'Good'
    return 'Rating'
  }, [rating])

  const subtitle = useMemo(() => ratingSubtitle || pluralizeToString('review', total), [ratingSubtitle, total])
  const ratingText = rating === 10 ? rating : rating.toFixed(1)

  let ratingElement: React.ReactNode
  if (variant === 'xl') {
    ratingElement = <Heading variant="heading2" colour="neutral-eight">{ratingText}</Heading>
  } else if (variant === 'large') {
    ratingElement = <Subtitle variant="subtitle2" colour="neutral-eight">{ratingText}</Subtitle>
  } else if (variant === 'medium') {
    ratingElement = <Heading variant="heading6" colour="neutral-eight">{ratingText}</Heading>
  } else if (variant === 'small') {
    ratingElement = <Caption variant="large" weight="bold" colour="neutral-eight">{ratingText}</Caption>
  } else {
    ratingElement = <BodyText variant="small" weight="bold" colour="neutral-eight">{ratingText}</BodyText>
  }

  return (
    <Group direction="horizontal" verticalAlign="center" gap={8} className={className}>
      <NumberContainer className={cn(`variant-${variant}`, {
        exclusive: rating >= 7,
        black: rating < 7,
      })}>
        {ratingElement}
      </NumberContainer>
      {!onlyNumber && (
        <Group
          direction={inlineLabel ? 'horizontal' : 'vertical'}
          verticalAlign={inlineLabel ? 'center' : undefined}
          horizontalAlign={inlineLabel ? undefined : 'start'}
          gap={inlineLabel ? 4 : undefined}
        >
          <BodyText
            variant={variant === 'xl' ? 'medium' : 'small'}
            weight="bold"
            colour="neutral-one"
          >
            {ratingLabel}
          </BodyText>
          {!hideLink && <>
            {link && (
              <TextLink size="small" href="#customer-reviews" onClick={onReviewSubTitleClick} weight="regular">{subtitle}</TextLink>
            )}
            {!link && (
              <BodyText variant="small" colour="neutral-three">{subtitle}</BodyText>
            )}
          </>
          }
        </Group>
      )}
    </Group>
  )
}

export default NumberRating
